import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Routes, Route } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle } from 'lucide-react';
import MeetJoseph from './pages/MeetJoseph';
import BlogPost from './pages/BlogPost';
import HeroSection from './components/HeroSection';
import SmartScopingSection from './components/SmartScopingSection';
import ServicesSection from './components/ServicesSection';
import ProductsSection from './components/ProductsSection';
import AnalysisSection from './components/AnalysisSection';
import FoundingOfferSection from './components/FoundingOfferSection';
import ContactSection from './components/ContactSection';
import Navigation from './components/Navigation';
import NavTransition from './components/NavTransition';
import ServiceDetailModal from './components/ServiceDetailModal';
import Footer from './components/Footer';
import './animations.css';

let initScrollAnimations, refreshScrollTriggers;
try {
  const scrollAnimations = require('./utils/scrollAnimations');
  initScrollAnimations = scrollAnimations.initScrollAnimations;
  refreshScrollTriggers = scrollAnimations.refreshScrollTriggers;
} catch (error) {
  console.warn('Scroll animations not available');
}

const SECTIONS = ['hero', 'smart-scoping', 'services', 'products', 'analysis', 'founding-offer', 'contact'];

export default function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [navTransition, setNavTransition] = useState({ active: false, label: '' });
  const [selectedService, setSelectedService] = useState(null);
  const containerRef = useRef(null);
  const cursorRef = useRef(null);

  // Active section tracker
  useEffect(() => {
    const observers = [];
    SECTIONS.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActiveSection(id); },
        { threshold: 0.35 }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, []);

  // Scroll-triggered enter animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('animate-in');
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    const els = document.querySelectorAll(
      '.fade-in-up, .slide-in-left, .slide-in-right, .scale-in, .card-lift-in, .card-fade-lift, .stagger-cards, .counter-enhanced, .text-reveal, .slide-left, .slide-right, .forge-divider, .forge-product-grid'
    );
    els.forEach((el) => observer.observe(el));
    return () => els.forEach((el) => observer.unobserve(el));
  }, []);

  // GSAP scroll animations
  useEffect(() => {
    if (!initScrollAnimations || !refreshScrollTriggers) return;
    const timer = setTimeout(() => initScrollAnimations(), 100);
    const onResize = () => refreshScrollTriggers();
    window.addEventListener('resize', onResize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', onResize);
      if (typeof window !== 'undefined' && window.ScrollTrigger) {
        window.ScrollTrigger.getAll().forEach((t) => t.kill());
      }
    };
  }, []);

  // Deep-link support
  useEffect(() => {
    if (!window.location.hash) return;
    const id = window.location.hash.slice(1);
    const timer = setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  // Cursor glow + spotlight/magnetic on cards
  useEffect(() => {
    const onMove = (e) => {
      if (cursorRef.current) {
        cursorRef.current.style.left = `${e.clientX}px`;
        cursorRef.current.style.top = `${e.clientY}px`;
        cursorRef.current.classList.add('active');
      }
      document.querySelectorAll('.card-spotlight').forEach((card) => {
        const rect = card.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        if (x >= 0 && x <= 100 && y >= 0 && y <= 100) {
          card.style.setProperty('--mouse-x', `${x}%`);
          card.style.setProperty('--mouse-y', `${y}%`);
          const cx = rect.left + rect.width / 2;
          const cy = rect.top + rect.height / 2;
          card.style.setProperty('--rotate-x', `${((e.clientY - cy) / (rect.height / 2)) * -6}deg`);
          card.style.setProperty('--rotate-y', `${((e.clientX - cx) / (rect.width / 2)) * 6}deg`);
        }
      });
    };
    const onLeave = () => cursorRef.current?.classList.remove('active');
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseleave', onLeave);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseleave', onLeave);
    };
  }, []);

  // Nav transition handler
  const handleNavClick = useCallback((sectionId) => {
    const item = [
      { id: 'hero',           name: 'Home'          },
      { id: 'smart-scoping',  name: 'Scoping'       },
      { id: 'services',       name: 'Services'      },
      { id: 'products',       name: 'Products'      },
      { id: 'analysis',       name: 'Analysis'      },
      { id: 'founding-offer', name: 'Founding Offer'},
      { id: 'contact',        name: 'Contact'       },
    ].find((x) => x.id === sectionId);

    setNavTransition({ active: true, label: item?.name ?? '' });
    setTimeout(() => {
      const el = document.getElementById(sectionId);
      if (el) window.scrollTo({ top: el.offsetTop - 72, behavior: 'instant' });
    }, 280);
    setTimeout(() => setNavTransition({ active: false, label: item?.name ?? '' }), 560);
  }, []);

  return (
    <Routes>
      <Route path="/meet-Joseph" element={<MeetJoseph />} />
      <Route path="/meet-Joseph/blog/:slug" element={<BlogPost />} />
      <Route path="*" element={<HomePage containerRef={containerRef} cursorRef={cursorRef} isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} navTransition={navTransition} selectedService={selectedService} setSelectedService={setSelectedService} handleNavClick={handleNavClick} activeSection={activeSection} />} />
    </Routes>
  );
}

function HomePage({ containerRef, cursorRef, isMenuOpen, setIsMenuOpen, navTransition, selectedService, setSelectedService, handleNavClick, activeSection }) {
  const WhatsAppButton = () => (
    <motion.div
      className="fixed bottom-6 right-6 z-50"
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ delay: 2, type: 'spring', stiffness: 200 }}
    >
      <motion.a
        href="https://wa.me/254754709099?text=Hi%20Tuneup%20Tech!%20I'm%20interested%20in%20your%20services"
        target="_blank"
        rel="noopener noreferrer"
        className="bg-green-500 hover:bg-green-600 text-white p-4 flex items-center justify-center group shadow-2xl"
        style={{ borderRadius: '2px' }}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.95 }}
      >
        <MessageCircle className="w-6 h-6" />
        <span
          className="absolute right-full mr-3 bg-gray-900 border border-gray-700 text-white px-3 py-1 text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity font-medium"
          style={{ borderRadius: '2px' }}
        >
          Chat with us!
        </span>
      </motion.a>
    </motion.div>
  );

  return (
    <div ref={containerRef} className="min-h-screen bg-gray-950 overflow-x-hidden">

      {/* Ambient cursor glow */}
      <div ref={cursorRef} className="cursor-glow" />

      {/* Grain texture overlay */}
      <div className="grain-texture" />

      {/* Nav transition flash panel */}
      <NavTransition active={navTransition.active} label={navTransition.label} />

      {/* Service detail modal */}
      <AnimatePresence>
        {selectedService && (
          <ServiceDetailModal
            serviceTitle={selectedService}
            onClose={() => setSelectedService(null)}
          />
        )}
      </AnimatePresence>

      {/* Navigation */}
      <Navigation
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        onNavClick={handleNavClick}
        activeSection={activeSection}
      />

      {/* Main Content */}
      <main className="relative z-10">
        <HeroSection onServiceClick={setSelectedService} />

        <SmartScopingSection />

        <div className="forge-divider fade-in-up" />

        <ServicesSection onServiceClick={setSelectedService} />

        <div className="forge-divider fade-in-up" />

        <ProductsSection />

        <div className="forge-divider fade-in-up" />

        <AnalysisSection />

        <div className="forge-divider fade-in-up" />

        <FoundingOfferSection />

        <div className="forge-divider fade-in-up" />

        <ContactSection />
      </main>

      <Footer />

      <WhatsAppButton />
    </div>
  );
}
